/* ============================================================
   Field accessors + client-side rollups for the v2 console.
   Everything here reads from the existing `BiothreatSignal`
   shape — no new data requirements. All aggregations run over
   the currently-loaded (filtered) page of rows.
   ============================================================ */
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import { formatNwssResolvedLocation } from "../nwssDisplayFormat"
import {
  V2_SEVERITY_ORDER,
  type V2NwssStatus,
  type V2Tier,
} from "./biothreatV2Tokens"

/** Default severity ladder (top-down). Source from config/admin in future. */
export const V2_SEVERITY_MINS: Record<V2Tier, number> = {
  severe: 90,
  high: 65,
  moderate: 40,
  low: 15,
}

/**
 * The GDELT impact floor currently in effect: the slider value when set,
 * otherwise the moderate default. This is both the sweep-reveal threshold and
 * the cutoff for what counts as "matching the filters" — the map fetches the
 * full noise field (no floor) for the reveal animation, so co-located groups
 * must be filtered to this floor before being shown in hovers/drawers.
 */
export const getGdeltImpactFloor = (impactScoreMin: string): number =>
  impactScoreMin ? Number(impactScoreMin) : V2_SEVERITY_MINS.moderate

/** Walk tiers most-severe-first; return the first whose threshold is met. */
export const classifyImpact = (impact: number | null | undefined): V2Tier => {
  const score = impact ?? 0
  for (const tier of V2_SEVERITY_ORDER) {
    if (score >= V2_SEVERITY_MINS[tier]) return tier
  }
  return "low"
}

export const getImpact = (row: BiothreatSignal): number => row.impact_score ?? 0

export const getDiseaseCode = (row: BiothreatSignal): string =>
  row.clean_disease_codes[0] ?? row.nwss_meta?.disease ?? "Unknown"

export const getDiseaseLabel = (row: BiothreatSignal): string =>
  getDiseaseCode(row).replace(/_/g, " ")

export const getLocationLabel = (row: BiothreatSignal): string => {
  if (row.source === "nwss") {
    return formatNwssResolvedLocation(row.resolved_location)
  }
  return row.resolved_location || "—"
}

export const getNwssStatus = (row: BiothreatSignal): V2NwssStatus | null =>
  (row.nwss_meta?.status as V2NwssStatus | undefined) ?? null

export const getNwssSites = (row: BiothreatSignal): number =>
  row.nwss_meta?.n_sites ?? 0

export const getNwssDetections = (row: BiothreatSignal): number =>
  row.nwss_meta?.n_detections ?? 0

/** Severity tallies for the GDELT aggregation strip. */
export const severityCounts = (
  rows: BiothreatSignal[]
): Record<V2Tier, number> => {
  const counts: Record<V2Tier, number> = {
    severe: 0,
    high: 0,
    moderate: 0,
    low: 0,
  }
  for (const row of rows) counts[classifyImpact(getImpact(row))] += 1
  return counts
}

/** Status tallies for the NWSS aggregation strip. */
export const statusCounts = (
  rows: BiothreatSignal[]
): Record<V2NwssStatus, number> => {
  const counts: Record<V2NwssStatus, number> = {
    start: 0,
    ongoing: 0,
    ended: 0,
  }
  for (const row of rows) {
    const status = getNwssStatus(row)
    if (status) counts[status] += 1
  }
  return counts
}

/**
 * Threat-condition index (0–100) for the GDELT gauge: the mean impact score of
 * the filtered rows. A simple, explainable rollup of "how hot is the current
 * view" with no new endpoint.
 */
export const threatIndex = (rows: BiothreatSignal[]): number => {
  if (rows.length === 0) return 0
  const total = rows.reduce((sum, row) => sum + getImpact(row), 0)
  return Math.round(Math.min(100, Math.max(0, total / rows.length)))
}

export type V2ThreatLevel = V2Tier

export const threatLevelFromIndex = (index: number): V2ThreatLevel =>
  index >= 75
    ? "severe"
    : index >= 55
      ? "high"
      : index >= 35
        ? "moderate"
        : "low"

export interface V2DiseaseRollup {
  disease: string
  diseaseLabel: string
  count: number
  locations: string[]
  /** Highest-impact signal in the group — the row a click should open. */
  lead: BiothreatSignal
  /** Worst severity tier present (GDELT) — drives tinting. */
  tier: V2Tier
}

/** Group the filtered rows by disease for the Top-Diseases rail. */
export const diseaseRollup = (rows: BiothreatSignal[]): V2DiseaseRollup[] => {
  const groups = new Map<
    string,
    { rows: BiothreatSignal[]; locations: Set<string> }
  >()
  for (const row of rows) {
    const key = getDiseaseCode(row)
    const group = groups.get(key) ?? { rows: [], locations: new Set() }
    group.rows.push(row)
    group.locations.add(getLocationLabel(row))
    groups.set(key, group)
  }

  return Array.from(groups.entries())
    .map(([disease, group]) => {
      const lead = group.rows.reduce((best, row) =>
        getImpact(row) > getImpact(best) ? row : best
      )
      const worstTier = group.rows.reduce<V2Tier>((worst, row) => {
        const tier = classifyImpact(getImpact(row))
        return V2_SEVERITY_ORDER.indexOf(tier) <
          V2_SEVERITY_ORDER.indexOf(worst)
          ? tier
          : worst
      }, "low")
      return {
        disease,
        diseaseLabel: disease.replace(/_/g, " "),
        count: group.rows.length,
        locations: Array.from(group.locations),
        lead,
        tier: worstTier,
      }
    })
    .sort(
      (a, b) =>
        b.count - a.count ||
        V2_SEVERITY_ORDER.indexOf(a.tier) - V2_SEVERITY_ORDER.indexOf(b.tier)
    )
}
